import _slicedToArray from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _inherits from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
import _classCallCheck from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/vercel/path0/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";

/**
 * lil-gui
 * https://lil-gui.georgealways.com
 * @version 0.11.0
 * @author George Michael Brower
 * @license MIT
 */

/**
 * Base class for all controllers.
 */
var Controller = /*#__PURE__*/function () {
  function Controller(parent, object, property, className) {
    var widgetTag = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 'div';

    _classCallCheck(this, Controller);

    /**
     * The GUI that contains this controller.
     * @type {GUI}
     */
    this.parent = parent;
    /**
     * The object this controller will modify.
     * @type {object}
     */

    this.object = object;
    /**
     * The name of the property to control.
     * @type {string}
     */

    this.property = property;
    /**
     * Used to determine if the controller is disabled.
     * Use `controller.disable( true|false )` to modify this value
     * @type {boolean}
     */

    this._disabled = false;
    /**
     * The value of `object[ property ]` when the controller was created.
     * @type {any}
     */

    this.initialValue = this.getValue();
    /**
     * The outermost container DOM element for this controller.
     * @type {HTMLElement}
     */

    this.domElement = document.createElement('div');
    this.domElement.classList.add('controller');
    this.domElement.classList.add(className);
    /**
     * The DOM element that contains the controller's name.
     * @type {HTMLElement}
     */

    this.$name = document.createElement('div');
    this.$name.classList.add('name');
    Controller.nextNameID = Controller.nextNameID || 0;
    this.$name.id = "lil-gui-name-".concat(++Controller.nextNameID);
    /**
     * The DOM element that contains the controller's "widget" (which differs by controller type).
     * @type {HTMLElement}
     */

    this.$widget = document.createElement(widgetTag);
    this.$widget.classList.add('widget');
    /**
     * The DOM element that receives the disabled attribute when using disable()
     * @type {HTMLElement}
     */

    this.$disable = this.$widget;
    this.domElement.appendChild(this.$name);
    this.domElement.appendChild(this.$widget);
    this.parent.children.push(this);
    this.parent.controllers.push(this);
    this.parent.$children.appendChild(this.domElement);
    this._listenCallback = this._listenCallback.bind(this);
    this.name(property);
  }
  /**
   * Sets the name of the controller and its label in the GUI.
   * @param {string} name
   * @returns {this}
   */


  _createClass(Controller, [{
    key: "name",
    value: function name(_name) {
      /**
       * The controller's name. Use `controller.name( 'Name' )` to modify this value.
       * @type {string}
       */
      this._name = _name;
      this.$name.innerHTML = _name;
      return this;
    }
    /**
     * Pass a function to be called whenever the value is modified by this controller.
     * The function receives the new value as its first parameter. The value of `this` will be the
     * controller.
     * @param {Function} callback
     * @returns {this}
     * @example
     * const controller = gui.add( object, 'property' );
     *
     * controller.onChange( v => {
     * 	console.log( 'The value is now ' + v );
     * 	console.assert( this === controller );
     * } );
     */

  }, {
    key: "onChange",
    value: function onChange(callback) {
      /**
       * Used to access the function bound to change events. Don't modify this value directly.
       * Use the `controller.onChange( callback )` method instead.
       * @type {Function}
       */
      this._onChange = callback;
      return this;
    }
  }, {
    key: "_callOnChange",
    value: function _callOnChange() {
      this.parent._callOnChange(this);

      if (this._onChange !== undefined) {
        this._onChange.call(this, this.getValue());
      }
    } // Provided for compatability

  }, {
    key: "onFinishChange",
    value: function onFinishChange(callback) {
      return this.onChange(callback);
    }
    /**
     * Sets the controller back to its initial value.
     * @returns {this}
     */

  }, {
    key: "reset",
    value: function reset() {
      this.setValue(this.initialValue);
      return this;
    }
    /**
     * Enables this controller.
     * @param {boolean} enabled
     * @returns {this}
     * @example
     * controller.enable();
     * controller.enable( false ); // disable
     * controller.enable( controller._disabled ); // toggle
     */

  }, {
    key: "enable",
    value: function enable() {
      var enabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      return this.disable(!enabled);
    }
    /**
     * Disables this controller.
     * @param {boolean} disabled
     * @returns {this}
     * @example
     * controller.disable();
     * controller.disable( false ); // enable
     * controller.disable( !controller._disabled ); // toggle
     */

  }, {
    key: "disable",
    value: function disable() {
      var disabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      if (disabled === this._disabled) return this;
      this._disabled = disabled;
      this.domElement.classList.toggle('disabled', disabled);

      if (disabled) {
        this.$disable.setAttribute('disabled', 'disabled');
      } else {
        this.$disable.removeAttribute('disabled');
      }

      return this;
    }
    /**
     * Destroys this controller and replaces it with a new option controller. Provided as a more
     * descriptive syntax for `gui.add`, but primarily for compatibility with dat.gui.
     *
     * Use caution, as this method will destroy old references to this controller. It will also
     * change controller order if called out of sequence, moving the option controller to the end of
     * the GUI.
     * @example
     * // safe usage
     *
     * gui.add( object1, 'property' ).options( [ 'a', 'b', 'c' ] );
     * gui.add( object2, 'property' );
     *
     * // danger
     *
     * const c = gui.add( object1, 'property' );
     * gui.add( object2, 'property' );
     *
     * c.options( [ 'a', 'b', 'c' ] );
     * // controller is now at the end of the GUI even though it was added first
     *
     * assert( c.parent.children.indexOf( c ) === -1 )
     * // c references a controller that no longer exists
     *
     * @param {object|Array} options
     * @returns {Controller}
     */

  }, {
    key: "options",
    value: function options(_options) {
      var controller = this.parent.add(this.object, this.property, _options);
      controller.name(this._name);
      this.destroy();
      return controller;
    }
    /**
     * Sets the minimum value. Only works on number controllers.
     * @param {number} min
     * @returns {this}
     */

  }, {
    key: "min",
    value: function min(_min) {
      return this;
    }
    /**
     * Sets the maximum value. Only works on number controllers.
     * @param {number} max
     * @returns {this}
     */

  }, {
    key: "max",
    value: function max(_max) {
      return this;
    }
    /**
     * Sets the step. Only works on number controllers.
     * @param {number} step
     * @returns {this}
     */

  }, {
    key: "step",
    value: function step(_step) {
      return this;
    }
    /**
     * Calls `updateDisplay()` every animation frame. Pass `false` to stop listening.
     * @param {boolean} listen
     * @returns {this}
     */

  }, {
    key: "listen",
    value: function listen() {
      var _listen = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      /**
       * Used to determine if the controller is currently listening. Don't modify this value
       * directly. Use the `controller.listen( true|false )` method instead.
       * @type {boolean}
       */
      this._listening = _listen;

      if (this._listenCallbackID !== undefined) {
        cancelAnimationFrame(this._listenCallbackID);
        this._listenCallbackID = undefined;
      }

      if (this._listening) {
        this._listenCallback();
      }

      return this;
    }
  }, {
    key: "_listenCallback",
    value: function _listenCallback() {
      this._listenCallbackID = requestAnimationFrame(this._listenCallback);
      var value = this.getValue(); // Only update the DOM if the value has changed. Controllers that control non-primitive data
      // types get updated every frame to avoid the complexity of comparing objects

      if (value !== this._listenValuePrev || Object(value) === value) {
        this.updateDisplay();
      }

      this._listenValuePrev = value;
    }
    /**
     * Returns `object[ property ]`.
     * @returns {any}
     */

  }, {
    key: "getValue",
    value: function getValue() {
      return this.object[this.property];
    }
    /**
     * Sets the value of `object[ property ]`, invokes any `onChange` handlers and updates the display.
     * @param {any} value
     * @returns {this}
     */

  }, {
    key: "setValue",
    value: function setValue(value) {
      this.object[this.property] = value;

      this._callOnChange();

      this.updateDisplay();
      return this;
    }
    /**
     * Updates the display to keep it in sync with the current value. Useful for updating your
     * controllers when their values have been modified outside of the GUI.
     * @returns {this}
     */

  }, {
    key: "updateDisplay",
    value: function updateDisplay() {
      return this;
    }
  }, {
    key: "load",
    value: function load(value) {
      this.setValue(value);
    }
  }, {
    key: "save",
    value: function save() {
      return this.getValue();
    }
    /**
     * Destroys this controller and removes it from the parent GUI.
     */

  }, {
    key: "destroy",
    value: function destroy() {
      this.parent.children.splice(this.parent.children.indexOf(this), 1);
      this.parent.controllers.splice(this.parent.controllers.indexOf(this), 1);
      this.parent.$children.removeChild(this.domElement);
    }
  }]);

  return Controller;
}();

var BooleanController = /*#__PURE__*/function (_Controller) {
  _inherits(BooleanController, _Controller);

  var _super = _createSuper(BooleanController);

  function BooleanController(parent, object, property) {
    var _this;

    _classCallCheck(this, BooleanController);

    _this = _super.call(this, parent, object, property, 'boolean', 'label');
    _this.$input = document.createElement('input');

    _this.$input.setAttribute('type', 'checkbox');

    _this.$widget.appendChild(_this.$input);

    _this.$input.addEventListener('change', function () {
      _this.setValue(_this.$input.checked);
    });

    _this.$disable = _this.$input;

    _this.updateDisplay();

    return _this;
  }

  _createClass(BooleanController, [{
    key: "updateDisplay",
    value: function updateDisplay() {
      this.$input.checked = this.getValue();
      return this;
    }
  }]);

  return BooleanController;
}(Controller);

function normalizeColorString(string) {
  var match, result;

  if (match = string.match(/(#|0x)?([a-f0-9]{6})/i)) {
    result = match[2];
  } else if (match = string.match(/rgb\(\s*(\d*)\s*,\s*(\d*)\s*,\s*(\d*)\s*\)/)) {
    result = parseInt(match[1]).toString(16).padStart(2, 0) + parseInt(match[2]).toString(16).padStart(2, 0) + parseInt(match[3]).toString(16).padStart(2, 0);
  } else if (match = string.match(/^#?([a-f0-9])([a-f0-9])([a-f0-9])$/i)) {
    result = match[1] + match[1] + match[2] + match[2] + match[3] + match[3];
  }

  if (result) {
    return '#' + result;
  }

  return false;
}

var STRING = {
  isPrimitive: true,
  match: function match(v) {
    return typeof v === 'string';
  },
  fromHexString: normalizeColorString,
  toHexString: normalizeColorString
};
var INT = {
  isPrimitive: true,
  match: function match(v) {
    return typeof v === 'number';
  },
  fromHexString: function fromHexString(string) {
    return parseInt(string.substring(1), 16);
  },
  toHexString: function toHexString(value) {
    return '#' + value.toString(16).padStart(6, 0);
  }
};
var ARRAY = {
  isPrimitive: false,
  match: Array.isArray,
  fromHexString: function fromHexString(string, target) {
    var rgbScale = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
    var int = INT.fromHexString(string);
    target[0] = (int >> 16 & 255) / 255 * rgbScale;
    target[1] = (int >> 8 & 255) / 255 * rgbScale;
    target[2] = (int & 255) / 255 * rgbScale;
  },
  toHexString: function toHexString(_ref) {
    var _ref2 = _slicedToArray(_ref, 3),
        r = _ref2[0],
        g = _ref2[1],
        b = _ref2[2];

    var rgbScale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    rgbScale = 255 / rgbScale;
    var int = r * rgbScale << 16 ^ g * rgbScale << 8 ^ b * rgbScale << 0;
    return INT.toHexString(int);
  }
};
var OBJECT = {
  isPrimitive: false,
  match: function match(v) {
    return Object(v) === v;
  },
  fromHexString: function fromHexString(string, target) {
    var rgbScale = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
    var int = INT.fromHexString(string);
    target.r = (int >> 16 & 255) / 255 * rgbScale;
    target.g = (int >> 8 & 255) / 255 * rgbScale;
    target.b = (int & 255) / 255 * rgbScale;
  },
  toHexString: function toHexString(_ref3) {
    var r = _ref3.r,
        g = _ref3.g,
        b = _ref3.b;
    var rgbScale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    rgbScale = 255 / rgbScale;
    var int = r * rgbScale << 16 ^ g * rgbScale << 8 ^ b * rgbScale << 0;
    return INT.toHexString(int);
  }
};
var FORMATS = [STRING, INT, ARRAY, OBJECT];

function getColorFormat(value) {
  return FORMATS.find(function (format) {
    return format.match(value);
  });
}

var ColorController = /*#__PURE__*/function (_Controller2) {
  _inherits(ColorController, _Controller2);

  var _super2 = _createSuper(ColorController);

  function ColorController(parent, object, property, rgbScale) {
    var _this2;

    _classCallCheck(this, ColorController);

    _this2 = _super2.call(this, parent, object, property, 'color');
    _this2.$input = document.createElement('input');

    _this2.$input.setAttribute('type', 'color');

    _this2.$input.setAttribute('tabindex', -1);

    _this2.$input.setAttribute('aria-labelledby', _this2.$name.id);

    _this2.$text = document.createElement('input');

    _this2.$text.setAttribute('type', 'text');

    _this2.$text.setAttribute('spellcheck', 'false');

    _this2.$text.setAttribute('aria-labelledby', _this2.$name.id);

    _this2.$display = document.createElement('div');

    _this2.$display.classList.add('display');

    _this2.$display.appendChild(_this2.$input);

    _this2.$widget.appendChild(_this2.$display);

    _this2.$widget.appendChild(_this2.$text);

    _this2._format = getColorFormat(_this2.initialValue);
    _this2._rgbScale = rgbScale;
    _this2._initialValueHexString = _this2.save();
    _this2._textFocused = false;

    var onInputChange = function onInputChange() {
      _this2._setValueFromHexString(_this2.$input.value);
    };

    _this2.$input.addEventListener('change', onInputChange);

    _this2.$input.addEventListener('input', onInputChange);

    _this2.$input.addEventListener('focus', function () {
      _this2.$display.classList.add('focus');
    });

    _this2.$input.addEventListener('blur', function () {
      _this2.$display.classList.remove('focus');
    });

    _this2.$text.addEventListener('input', function () {
      var tryParse = normalizeColorString(_this2.$text.value);

      if (tryParse) {
        _this2._setValueFromHexString(tryParse);
      }
    });

    _this2.$text.addEventListener('focus', function () {
      _this2._textFocused = true;

      _this2.$text.select();
    });

    _this2.$text.addEventListener('blur', function () {
      _this2._textFocused = false;

      _this2.updateDisplay();
    });

    _this2.$disable = _this2.$text;

    _this2.updateDisplay();

    return _this2;
  }

  _createClass(ColorController, [{
    key: "reset",
    value: function reset() {
      this._setValueFromHexString(this._initialValueHexString);

      return this;
    }
  }, {
    key: "_setValueFromHexString",
    value: function _setValueFromHexString(value) {
      if (this._format.isPrimitive) {
        var newValue = this._format.fromHexString(value);

        this.setValue(newValue);
      } else {
        this._format.fromHexString(value, this.getValue(), this._rgbScale);

        this._callOnChange();

        this.updateDisplay();
      }
    }
  }, {
    key: "save",
    value: function save() {
      return this._format.toHexString(this.getValue(), this._rgbScale);
    }
  }, {
    key: "load",
    value: function load(value) {
      this._setValueFromHexString(value);
    }
  }, {
    key: "updateDisplay",
    value: function updateDisplay() {
      this.$input.value = this._format.toHexString(this.getValue(), this._rgbScale);

      if (!this._textFocused) {
        this.$text.value = this.$input.value.substring(1);
      }

      this.$display.style.backgroundColor = this.$input.value;
      return this;
    }
  }]);

  return ColorController;
}(Controller);

var FunctionController = /*#__PURE__*/function (_Controller3) {
  _inherits(FunctionController, _Controller3);

  var _super3 = _createSuper(FunctionController);

  function FunctionController(parent, object, property) {
    var _this3;

    _classCallCheck(this, FunctionController);

    _this3 = _super3.call(this, parent, object, property, 'function'); // Buttons are the only case where widget contains name

    _this3.$button = document.createElement('button');

    _this3.$button.appendChild(_this3.$name);

    _this3.$widget.appendChild(_this3.$button);

    _this3.$button.addEventListener('click', function (e) {
      e.preventDefault();

      _this3.getValue().call(_this3.object);
    }); // enables :active pseudo class on mobile


    _this3.$button.addEventListener('touchstart', function () {});

    _this3.$disable = _this3.$button;
    return _this3;
  }

  return FunctionController;
}(Controller);

var NumberController = /*#__PURE__*/function (_Controller4) {
  _inherits(NumberController, _Controller4);

  var _super4 = _createSuper(NumberController);

  function NumberController(parent, object, property, min, max, step) {
    var _this4;

    _classCallCheck(this, NumberController);

    _this4 = _super4.call(this, parent, object, property, 'number');

    _this4._initInput();

    _this4.min(min);

    _this4.max(max);

    var stepExplicit = step !== undefined;

    _this4.step(stepExplicit ? step : _this4._getImplicitStep(), stepExplicit);

    _this4.updateDisplay();

    return _this4;
  }

  _createClass(NumberController, [{
    key: "min",
    value: function min(_min2) {
      this._min = _min2;

      this._onUpdateMinMax();

      return this;
    }
  }, {
    key: "max",
    value: function max(_max2) {
      this._max = _max2;

      this._onUpdateMinMax();

      return this;
    }
  }, {
    key: "step",
    value: function step(_step2) {
      var explicit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      this._step = _step2;
      this._stepExplicit = explicit;
      return this;
    }
  }, {
    key: "updateDisplay",
    value: function updateDisplay() {
      var value = this.getValue();

      if (this._hasSlider) {
        var percent = (value - this._min) / (this._max - this._min);
        this.$fill.style.setProperty('width', percent * 100 + '%');
      }

      if (!this._inputFocused) {
        this.$input.value = value;
      }

      return this;
    }
  }, {
    key: "_initInput",
    value: function _initInput() {
      var _this5 = this;

      this.$input = document.createElement('input');
      this.$input.setAttribute('type', 'text');
      this.$input.setAttribute('inputmode', 'numeric');
      this.$input.setAttribute('aria-labelledby', this.$name.id);
      this.$widget.appendChild(this.$input);
      this.$disable = this.$input;

      var onInput = function onInput() {
        var value = parseFloat(_this5.$input.value);
        if (isNaN(value)) return;

        _this5.setValue(_this5._clamp(value));
      }; // invoked on wheel or arrow key up/down


      var increment = function increment(delta) {
        var value = parseFloat(_this5.$input.value);
        if (isNaN(value)) return;

        _this5._snapClampSetValue(value + delta); // Force the input to updateDisplay when it's focused


        _this5.$input.value = _this5.getValue();
      };

      var onKeyDown = function onKeyDown(e) {
        if (e.code === 'Enter') {
          _this5.$input.blur();
        }

        if (e.code === 'ArrowUp') {
          e.preventDefault();
          increment(_this5._step * _this5._arrowKeyMultiplier(e));
        }

        if (e.code === 'ArrowDown') {
          e.preventDefault();
          increment(-1 * _this5._step * _this5._arrowKeyMultiplier(e));
        }
      };

      var onWheel = function onWheel(e) {
        if (_this5._inputFocused) {
          e.preventDefault();
          increment(_this5._normalizeMouseWheel(e) * _this5._step);
        }
      };

      var onFocus = function onFocus() {
        _this5._inputFocused = true;
      };

      var onBlur = function onBlur() {
        _this5._inputFocused = false;

        _this5.updateDisplay();
      };

      this.$input.addEventListener('focus', onFocus);
      this.$input.addEventListener('input', onInput);
      this.$input.addEventListener('blur', onBlur);
      this.$input.addEventListener('keydown', onKeyDown);
      this.$input.addEventListener('wheel', onWheel, {
        passive: false
      });
    }
  }, {
    key: "_initSlider",
    value: function _initSlider() {
      var _this6 = this;

      this._hasSlider = true; // Build DOM
      // ---------------------------------------------------------------------

      this.$slider = document.createElement('div');
      this.$slider.classList.add('slider');
      this.$fill = document.createElement('div');
      this.$fill.classList.add('fill');
      this.$slider.appendChild(this.$fill);
      this.$widget.insertBefore(this.$slider, this.$input);
      this.domElement.classList.add('hasSlider'); // Map clientX to value
      // ---------------------------------------------------------------------

      var map = function map(v, a, b, c, d) {
        return (v - a) / (b - a) * (d - c) + c;
      };

      var setValueFromX = function setValueFromX(clientX) {
        var rect = _this6.$slider.getBoundingClientRect();

        var value = map(clientX, rect.left, rect.right, _this6._min, _this6._max);

        _this6._snapClampSetValue(value);
      }; // Bind mouse listeners
      // ---------------------------------------------------------------------


      var mouseDown = function mouseDown(e) {
        setValueFromX(e.clientX);

        _this6._setActiveStyle(true);

        window.addEventListener('mousemove', mouseMove);
        window.addEventListener('mouseup', mouseUp);
      };

      var mouseMove = function mouseMove(e) {
        setValueFromX(e.clientX);
      };

      var mouseUp = function mouseUp() {
        _this6._setActiveStyle(false);

        window.removeEventListener('mousemove', mouseMove);
        window.removeEventListener('mouseup', mouseUp);
      };

      this.$slider.addEventListener('mousedown', mouseDown); // Bind touch listeners
      // ---------------------------------------------------------------------

      var testingForScroll = false,
          prevClientX,
          prevClientY;

      var onTouchStart = function onTouchStart(e) {
        if (e.touches.length > 1) return; // If we're in a scrollable container, we should wait for the first
        // touchmove to see if the user is trying to slide or scroll.

        if (_this6._hasScrollBar) {
          prevClientX = e.touches[0].clientX;
          prevClientY = e.touches[0].clientY;
          testingForScroll = true;
        } else {
          // Otherwise, we can set the value straight away on touchstart.
          e.preventDefault();
          setValueFromX(e.touches[0].clientX);

          _this6._setActiveStyle(true);

          testingForScroll = false;
        }

        window.addEventListener('touchmove', onTouchMove, {
          passive: false
        });
        window.addEventListener('touchend', onTouchEnd);
      };

      var onTouchMove = function onTouchMove(e) {
        if (testingForScroll) {
          var dx = e.touches[0].clientX - prevClientX;
          var dy = e.touches[0].clientY - prevClientY;

          if (Math.abs(dx) > Math.abs(dy)) {
            // We moved horizontally, set the value and stop checking.
            e.preventDefault();
            setValueFromX(e.touches[0].clientX);

            _this6._setActiveStyle(true);

            testingForScroll = false;
          } else {
            // This was, in fact, an attempt to scroll. Abort.
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
          }
        } else {
          e.preventDefault();
          setValueFromX(e.touches[0].clientX);
        }
      };

      var onTouchEnd = function onTouchEnd() {
        _this6._setActiveStyle(false);

        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onTouchEnd);
      };

      this.$slider.addEventListener('touchstart', onTouchStart); // Bind wheel listeners
      // ---------------------------------------------------------------------

      var onWheel = function onWheel(e) {
        // ignore vertical wheels if there's a scrollbar
        var isVertical = Math.abs(e.deltaX) < Math.abs(e.deltaY);
        if (isVertical && _this6._hasScrollBar) return;
        e.preventDefault();

        var delta = _this6._normalizeMouseWheel(e) * _this6._step;

        _this6._snapClampSetValue(_this6.getValue() + delta);
      };

      this.$slider.addEventListener('wheel', onWheel, {
        passive: false
      });
    }
  }, {
    key: "_setActiveStyle",
    value: function _setActiveStyle(active) {
      this.$slider.classList.toggle('active', active);
      document.body.classList.toggle('lil-gui-slider-active', active);
    }
  }, {
    key: "_getImplicitStep",
    value: function _getImplicitStep() {
      if (this._hasMin && this._hasMax) {
        return (this._max - this._min) / 1000;
      }

      return 0.1;
    }
  }, {
    key: "_onUpdateMinMax",
    value: function _onUpdateMinMax() {
      if (!this._hasSlider && this._hasMin && this._hasMax) {
        // If this is the first time we're hearing about min and max
        // and we haven't explicitly stated what our step is, let's
        // update that too.
        if (!this._stepExplicit) {
          this.step(this._getImplicitStep(), false);
        }

        this._initSlider();

        this.updateDisplay();
      }
    }
  }, {
    key: "_normalizeMouseWheel",
    value: function _normalizeMouseWheel(e) {
      var deltaX = e.deltaX,
          deltaY = e.deltaY; // 2019: Safari and Chrome report weird non-integral values for an actual
      // mouse with a wheel connected to my 2015 macbook, but still expose actual
      // lines scrolled via wheelDelta.

      if (Math.floor(e.deltaY) !== e.deltaY && e.wheelDelta) {
        deltaX = 0;
        deltaY = -e.wheelDelta / 120;
      }

      var wheel = deltaX + -deltaY;
      return wheel;
    }
  }, {
    key: "_arrowKeyMultiplier",
    value: function _arrowKeyMultiplier(e) {
      if (this._stepExplicit) {
        return e.shiftKey ? 10 : 1;
      } else if (e.shiftKey) {
        return 100;
      } else if (e.altKey) {
        return 1;
      }

      return 10;
    }
  }, {
    key: "_snap",
    value: function _snap(value) {
      // This would be the logical way to do things, but floating point errors.
      // return Math.round( value / this._step ) * this._step;
      // Using inverse step solves a lot of them, but not all
      // const inverseStep = 1 / this._step;
      // return Math.round( value * inverseStep ) / inverseStep;
      // Not happy about this, but haven't seen it break.
      var r = Math.round(value / this._step) * this._step;

      return parseFloat(r.toPrecision(15));
    }
  }, {
    key: "_clamp",
    value: function _clamp(value) {
      var min = this._hasMin ? this._min : -Infinity;
      var max = this._hasMax ? this._max : Infinity;
      return Math.max(min, Math.min(max, value));
    }
  }, {
    key: "_snapClampSetValue",
    value: function _snapClampSetValue(value) {
      this.setValue(this._clamp(this._snap(value)));
    }
  }, {
    key: "_hasScrollBar",
    get: function get() {
      var root = this.parent.root.$children;
      return root.scrollHeight > root.clientHeight;
    }
  }, {
    key: "_hasMin",
    get: function get() {
      return this._min !== undefined;
    }
  }, {
    key: "_hasMax",
    get: function get() {
      return this._max !== undefined;
    }
  }]);

  return NumberController;
}(Controller);

var OptionController = /*#__PURE__*/function (_Controller5) {
  _inherits(OptionController, _Controller5);

  var _super5 = _createSuper(OptionController);

  function OptionController(parent, object, property, options) {
    var _this7;

    _classCallCheck(this, OptionController);

    _this7 = _super5.call(this, parent, object, property, 'option');
    _this7.$select = document.createElement('select');

    _this7.$select.setAttribute('aria-labelledby', _this7.$name.id);

    _this7.$display = document.createElement('div');

    _this7.$display.classList.add('display');

    _this7._values = Array.isArray(options) ? options : Object.values(options);
    _this7._names = Array.isArray(options) ? options : Object.keys(options);

    _this7._names.forEach(function (name) {
      var $option = document.createElement('option');
      $option.innerHTML = name;

      _this7.$select.appendChild($option);
    });

    _this7.$select.addEventListener('change', function () {
      _this7.setValue(_this7._values[_this7.$select.selectedIndex]);
    });

    _this7.$select.addEventListener('focus', function () {
      _this7.$display.classList.add('focus');
    });

    _this7.$select.addEventListener('blur', function () {
      _this7.$display.classList.remove('focus');
    });

    _this7.$widget.appendChild(_this7.$select);

    _this7.$widget.appendChild(_this7.$display);

    _this7.$disable = _this7.$select;

    _this7.updateDisplay();

    return _this7;
  }

  _createClass(OptionController, [{
    key: "updateDisplay",
    value: function updateDisplay() {
      var value = this.getValue();

      var index = this._values.indexOf(value);

      this.$select.selectedIndex = index;
      this.$display.innerHTML = index === -1 ? value : this._names[index];
      return this;
    }
  }]);

  return OptionController;
}(Controller);

var StringController = /*#__PURE__*/function (_Controller6) {
  _inherits(StringController, _Controller6);

  var _super6 = _createSuper(StringController);

  function StringController(parent, object, property) {
    var _this8;

    _classCallCheck(this, StringController);

    _this8 = _super6.call(this, parent, object, property, 'string');
    _this8.$input = document.createElement('input');

    _this8.$input.setAttribute('type', 'text');

    _this8.$input.setAttribute('aria-labelledby', _this8.$name.id);

    _this8.$input.addEventListener('input', function () {
      _this8.setValue(_this8.$input.value);
    });

    _this8.$input.addEventListener('keydown', function (e) {
      if (e.code === 'Enter') {
        _this8.$input.blur();
      }
    });

    _this8.$widget.appendChild(_this8.$input);

    _this8.$disable = _this8.$input;

    _this8.updateDisplay();

    return _this8;
  }

  _createClass(StringController, [{
    key: "updateDisplay",
    value: function updateDisplay() {
      this.$input.value = this.getValue();
      return this;
    }
  }]);

  return StringController;
}(Controller);

var stylesheet = ".lil-gui {\n  font-family: var(--font-family);\n  font-size: var(--font-size);\n  line-height: 1;\n  font-weight: normal;\n  font-style: normal;\n  text-align: left;\n  background-color: var(--background-color);\n  color: var(--text-color);\n  user-select: none;\n  -webkit-user-select: none;\n  touch-action: manipulation;\n  --background-color: #1f1f1f;\n  --text-color: #ebebeb;\n  --title-background-color: #111111;\n  --title-text-color: #ebebeb;\n  --widget-color: #424242;\n  --hover-color: #4f4f4f;\n  --focus-color: #595959;\n  --number-color: #2cc9ff;\n  --string-color: #a2db3c;\n  --font-size: 11px;\n  --input-font-size: 11px;\n  --font-family: -apple-system, BlinkMacSystemFont, \"Lucida Grande\", \"Segoe UI\", Roboto, Arial, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\";\n  --font-family-mono: Menlo, Monaco, Consolas, \"Droid Sans Mono\", monospace, \"Droid Sans Fallback\";\n  --padding: 4px;\n  --spacing: 4px;\n  --widget-height: 20px;\n  --name-width: 45%;\n  --slider-knob-width: 2px;\n  --slider-input-width: 27%;\n  --color-input-width: 27%;\n  --slider-input-min-width: 45px;\n  --color-input-min-width: 45px;\n  --folder-indent: 7px;\n  --widget-padding: 0 0 0 3px;\n  --widget-border-radius: 2px;\n  --checkbox-size: calc(0.75 * var(--widget-height));\n  --scrollbar-width: 5px;\n}\n.lil-gui, .lil-gui * {\n  box-sizing: border-box;\n  margin: 0;\n}\n.lil-gui.root {\n  width: var(--width, 245px);\n  display: flex;\n  flex-direction: column;\n}\n.lil-gui.root > .title {\n  background: var(--title-background-color);\n  color: var(--title-text-color);\n}\n.lil-gui.root > .children {\n  overflow: auto;\n}\n.lil-gui.root > .children::-webkit-scrollbar {\n  width: var(--scrollbar-width);\n  height: var(--scrollbar-width);\n  background: var(--background-color);\n}\n.lil-gui.root > .children::-webkit-scrollbar-thumb {\n  border-radius: var(--scrollbar-width);\n  background: var(--focus-color);\n}\n.lil-gui .lil-gui {\n  --background-color: inherit;\n  --text-color: inherit;\n  --title-background-color: inherit;\n  --title-text-color: inherit;\n  --widget-color: inherit;\n  --hover-color: inherit;\n  --focus-color: inherit;\n  --number-color: inherit;\n  --string-color: inherit;\n  --font-size: inherit;\n  --input-font-size: inherit;\n  --font-family: inherit;\n  --font-family-mono: inherit;\n  --padding: inherit;\n  --spacing: inherit;\n  --widget-height: inherit;\n  --name-width: inherit;\n  --slider-knob-width: inherit;\n  --slider-input-width: inherit;\n  --color-input-width: inherit;\n  --slider-input-min-width: inherit;\n  --color-input-min-width: inherit;\n  --folder-indent: inherit;\n  --widget-padding: inherit;\n  --widget-border-radius: inherit;\n  --checkbox-size: inherit;\n}\n@media (pointer: coarse) {\n  .lil-gui.allow-touch-styles {\n    --widget-height: 28px;\n    --padding: 6px;\n    --spacing: 6px;\n    --font-size: 13px;\n    --input-font-size: 16px;\n    --folder-indent: 10px;\n    --widget-padding: 0 0 0 3px;\n    --scrollbar-width: 7px;\n    --slider-input-min-width: 50px;\n    --color-input-min-width: 65px;\n  }\n}\n.lil-gui.force-touch-styles {\n  --widget-height: 28px;\n  --padding: 6px;\n  --spacing: 6px;\n  --font-size: 13px;\n  --input-font-size: 16px;\n  --folder-indent: 10px;\n  --widget-padding: 0 0 0 3px;\n  --scrollbar-width: 7px;\n  --slider-input-min-width: 50px;\n  --color-input-min-width: 65px;\n}\n.lil-gui.autoPlace {\n  max-height: 100%;\n  position: fixed;\n  top: 0;\n  right: 15px;\n  z-index: 1001;\n}\n\n.lil-gui .controller {\n  display: flex;\n  align-items: center;\n  padding: 0 var(--padding);\n  margin: var(--spacing) 0;\n}\n.lil-gui .controller.disabled {\n  opacity: 0.5;\n}\n.lil-gui .controller.disabled, .lil-gui .controller.disabled * {\n  pointer-events: none !important;\n}\n.lil-gui .controller .name {\n  min-width: var(--name-width);\n  flex-shrink: 0;\n  white-space: pre;\n  padding-right: var(--spacing);\n  line-height: var(--widget-height);\n}\n.lil-gui .controller .widget {\n  position: relative;\n  display: flex;\n  align-items: center;\n  width: 100%;\n  min-height: var(--widget-height);\n}\n.lil-gui .controller.function .name {\n  line-height: unset;\n  padding: 0;\n}\n.lil-gui .controller.string input {\n  color: var(--string-color);\n}\n.lil-gui .controller.boolean .widget {\n  cursor: pointer;\n}\n.lil-gui .controller.color .display {\n  width: 100%;\n  height: var(--widget-height);\n  border-radius: var(--widget-border-radius);\n  position: relative;\n}\n@media (hover: hover) {\n  .lil-gui .controller.color .display:hover:before {\n    content: \" \";\n    display: block;\n    position: absolute;\n    border-radius: var(--widget-border-radius);\n    border: 1px solid #fff9;\n    left: 0;\n    right: 0;\n    top: 0;\n    bottom: 0;\n  }\n}\n.lil-gui .controller.color input[type=color] {\n  opacity: 0;\n  width: 100%;\n  height: 100%;\n  cursor: pointer;\n}\n.lil-gui .controller.color input[type=text] {\n  margin-left: var(--spacing);\n  font-family: var(--font-family-mono);\n  min-width: var(--color-input-min-width);\n  width: var(--color-input-width);\n  flex-shrink: 0;\n}\n.lil-gui .controller.option select {\n  opacity: 0;\n  position: absolute;\n  width: 100%;\n  max-width: 100%;\n}\n.lil-gui .controller.option .display {\n  position: relative;\n  pointer-events: none;\n  border-radius: var(--widget-border-radius);\n  height: var(--widget-height);\n  line-height: var(--widget-height);\n  max-width: 100%;\n  overflow: hidden;\n  word-break: break-all;\n  padding-left: 0.55em;\n  padding-right: 1.75em;\n  background: var(--widget-color);\n}\n@media (hover: hover) {\n  .lil-gui .controller.option .display.focus {\n    background: var(--focus-color);\n  }\n}\n.lil-gui .controller.option .display.active {\n  background: var(--focus-color);\n}\n.lil-gui .controller.option .display:after {\n  font-family: \"lil-gui\";\n  content: \"\u2195\";\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  padding-right: 0.375em;\n}\n.lil-gui .controller.option .widget,\n.lil-gui .controller.option select {\n  cursor: pointer;\n}\n@media (hover: hover) {\n  .lil-gui .controller.option .widget:hover .display {\n    background: var(--hover-color);\n  }\n}\n.lil-gui .controller.number input {\n  color: var(--number-color);\n}\n.lil-gui .controller.number.hasSlider input {\n  margin-left: var(--spacing);\n  width: var(--slider-input-width);\n  min-width: var(--slider-input-min-width);\n  flex-shrink: 0;\n}\n.lil-gui .controller.number .slider {\n  width: 100%;\n  height: var(--widget-height);\n  background-color: var(--widget-color);\n  border-radius: var(--widget-border-radius);\n  padding-right: var(--slider-knob-width);\n  overflow: hidden;\n  cursor: ew-resize;\n  touch-action: pan-y;\n}\n@media (hover: hover) {\n  .lil-gui .controller.number .slider:hover {\n    background-color: var(--hover-color);\n  }\n}\n.lil-gui .controller.number .slider.active {\n  background-color: var(--focus-color);\n}\n.lil-gui .controller.number .slider.active .fill {\n  opacity: 0.95;\n}\n.lil-gui .controller.number .fill {\n  height: 100%;\n  border-right: var(--slider-knob-width) solid var(--number-color);\n  box-sizing: content-box;\n}\n\n.lil-gui-slider-active .lil-gui {\n  --hover-color: var(--widget-color);\n}\n.lil-gui-slider-active * {\n  cursor: ew-resize !important;\n}\n\n.lil-gui .title {\n  --title-height: calc(var(--widget-height) + var(--spacing) * 1.25);\n  height: var(--title-height);\n  line-height: calc(var(--title-height) - 4px);\n  font-weight: 600;\n  padding: 0 var(--padding);\n  -webkit-tap-highlight-color: transparent;\n  cursor: pointer;\n  outline: none;\n  text-decoration-skip: objects;\n}\n.lil-gui .title:before {\n  font-family: \"lil-gui\";\n  content: \"\u25BE\";\n  padding-right: 2px;\n  display: inline-block;\n}\n.lil-gui .title:active {\n  background: var(--title-background-color);\n  opacity: 0.75;\n}\n@media (hover: hover) {\n  .lil-gui .title:hover {\n    background: var(--title-background-color);\n    opacity: 0.85;\n  }\n  .lil-gui .title:focus {\n    text-decoration: underline var(--focus-color);\n  }\n}\n.lil-gui.root > .title:focus {\n  text-decoration: none !important;\n}\n.lil-gui.closed > .title:before {\n  content: \"\u25B8\";\n}\n.lil-gui.closed > .children {\n  transform: translateY(-7px);\n  opacity: 0;\n}\n.lil-gui.closed:not(.transition) > .children {\n  display: none;\n}\n.lil-gui.transition > .children {\n  transition-duration: 300ms;\n  transition-property: height, opacity, transform;\n  transition-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n  overflow: hidden;\n  pointer-events: none;\n}\n.lil-gui .children:empty:before {\n  content: \"Empty\";\n  padding: 0 var(--padding);\n  margin: var(--spacing) 0;\n  display: block;\n  height: var(--widget-height);\n  font-style: italic;\n  line-height: var(--widget-height);\n  opacity: 0.5;\n}\n.lil-gui.root > .children > .lil-gui > .title {\n  border: 0 solid var(--widget-color);\n  border-width: 1px 0;\n  transition: border-color 300ms;\n}\n.lil-gui.root > .children > .lil-gui.closed > .title {\n  border-bottom-color: transparent;\n}\n.lil-gui + .controller {\n  border-top: 1px solid var(--widget-color);\n  margin-top: 0;\n  padding-top: var(--spacing);\n}\n.lil-gui .lil-gui .lil-gui > .title {\n  border: none;\n}\n.lil-gui .lil-gui .lil-gui > .children {\n  border: none;\n  margin-left: var(--folder-indent);\n  border-left: 2px solid var(--widget-color);\n}\n.lil-gui .lil-gui .controller {\n  border: none;\n}\n\n.lil-gui input {\n  -webkit-tap-highlight-color: transparent;\n  border: 0;\n  outline: none;\n  font-family: var(--font-family);\n  font-size: var(--input-font-size);\n  border-radius: var(--widget-border-radius);\n  height: var(--widget-height);\n  background: var(--widget-color);\n  color: var(--text-color);\n  width: 100%;\n}\n@media (hover: hover) {\n  .lil-gui input:hover {\n    background: var(--hover-color);\n  }\n  .lil-gui input:active {\n    background: var(--focus-color);\n  }\n}\n.lil-gui input[type=text] {\n  padding: var(--widget-padding);\n}\n.lil-gui input[type=text]:focus {\n  background: var(--focus-color);\n}\n.lil-gui input[type=checkbox] {\n  appearance: none;\n  -webkit-appearance: none;\n  height: var(--checkbox-size);\n  width: var(--checkbox-size);\n  border-radius: var(--widget-border-radius);\n  text-align: center;\n}\n.lil-gui input[type=checkbox]:checked:before {\n  font-family: \"lil-gui\";\n  content: \"\u2713\";\n  font-size: var(--checkbox-size);\n  line-height: var(--checkbox-size);\n}\n@media (hover: hover) {\n  .lil-gui input[type=checkbox]:focus {\n    box-shadow: inset 0 0 0 1px var(--focus-color);\n  }\n}\n.lil-gui button {\n  -webkit-tap-highlight-color: transparent;\n  outline: none;\n  cursor: pointer;\n  font-family: var(--font-family);\n  font-size: var(--font-size);\n  color: var(--text-color);\n  width: 100%;\n  height: var(--widget-height);\n  text-transform: none;\n  background: var(--widget-color);\n  border-radius: var(--widget-border-radius);\n  border: 1px solid var(--widget-color);\n  text-align: center;\n  line-height: calc(var(--widget-height) * 0.725);\n}\n@media (hover: hover) {\n  .lil-gui button:hover {\n    background: var(--hover-color);\n    border-color: var(--hover-color);\n  }\n  .lil-gui button:focus {\n    border-color: var(--focus-color);\n  }\n}\n.lil-gui button:active {\n  background: var(--focus-color);\n}\n\n@font-face {\n  font-family: \"lil-gui\";\n  src: url(\"data:application/font-woff;charset=utf-8;base64,d09GRgABAAAAAAUsAAsAAAAACJwAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAABHU1VCAAABCAAAAH4AAADAImwmYE9TLzIAAAGIAAAAPwAAAGBKqH5SY21hcAAAAcgAAAD0AAACrukyyJBnbHlmAAACvAAAAF8AAACEIZ5WI2hlYWQAAAMcAAAAJwAAADZfcj23aGhlYQAAA0QAAAAYAAAAJAC5AHhobXR4AAADXAAAABAAAABMAZAAAGxvY2EAAANsAAAAFAAAACgCEgIybWF4cAAAA4AAAAAeAAAAIAEfABJuYW1lAAADoAAAASIAAAIK9SUU/XBvc3QAAATEAAAAZgAAAJCTcMc2eJxVjbEOgjAURU+hFRBK1dGRL+ALnAiToyMLEzFpnPz/eAshwSa97517c/MwwJmeB9kwPl+0cf5+uGPZXsqPu4nvZabcSZldZ6kfyWnomFY/eScKqZNWupKJO6kXN3K9uCVoL7iInPr1X5baXs3tjuMqCtzEuagm/AAlzQgPAAB4nGNgYRBlnMDAysDAYM/gBiT5oLQBAwuDJAMDEwMrMwNWEJDmmsJwgCFeXZghBcjlZMgFCzOiKOIFAB71Bb8AeJy1kjFuwkAQRZ+DwRAwBtNQRUGKQ8OdKCAWUhAgKLhIuAsVSpWz5Bbkj3dEgYiUIszqWdpZe+Z7/wB1oCYmIoboiwiLT2WjKl/jscrHfGg/pKdMkyklC5Zs2LEfHYpjcRoPzme9MWWmk3dWbK9ObkWkikOetJ554fWyoEsmdSlt+uR0pCJR34b6t/TVg1SY3sYvdf8vuiKrpyaDXDISiegp17p7579Gp3p++y7HPAiY9pmTibljrr85qSidtlg4+l25GLCaS8e6rRxNBmsnERunKbaOObRz7N72ju5vdAjYpBXHgJylOAVsMseDAPEP8LYoUHicY2BiAAEfhjAGJgZWBgZ7RnFRdnVJELCRlBSRlATJMoLV2DK4glSYs6ubq5vbKrJLSbGrgEmovDuDJVhe3VzcXFwNLCOILB/C4IuQ1xTn5FPilBTj5FPmBAB76woyAHicY2BkYGAA4sklsQ/j+W2+MnAzpDBgAyEMYUCSg4EJxAEAvVwFCgB4nGNgZGBgSGFggJMhDIwMqEAYAByHATJ4nGNgAIIUNEwmAABl3AGReJxjYAACIQYlBiMGJ3wQAEcQBEV4nGNgZGBgEGZgY2BiAAEQyQWEDAz/wXwGAAsPATIAAHicXdBNSsNAHAXwl35iA0UQXYnMShfS9GPZA7T7LgIu03SSpkwzYTIt1BN4Ak/gKTyAeCxfw39jZkjymzcvAwmAW/wgwHUEGDb36+jQQ3GXGot79L24jxCP4gHzF/EIr4jEIe7wxhOC3g2TMYy4Q7+Lu/SHuEd/ivt4wJd4wPxbPEKMX3GI5+DJFGaSn4qNzk8mcbKSR6xdXdhSzaOZJGtdapd4vVPbi6rP+cL7TGXOHtXKll4bY1Xl7EGnPtp7Xy2n00zyKLVHfkHBa4IcJ2oD3cgggWvt/V/FbDrUlEUJhTn/0azVWbNTNr0Ens8de1tceK9xZmfB1CPjOmPH4kitmvOubcNpmVTN3oFJyjzCvnmrwhJTzqzVj9jiSX911FjeAAB4nG3HMRKCMBBA0f0giiKi4DU8k0V2GWbIZDOh4PoWWvq6J5V8If9NVNQcaDhyouXMhY4rPTcG7jwYmXhKq8Wz+p762aNaeYXom2n3m2dLTVgsrCgFJ7OTmIkYbwIbC6vIB7WmFfAAAA==\") format(\"woff\");\n}";

function _injectStyles(cssContent) {
  var injected = document.createElement('style');
  injected.innerHTML = cssContent;
  var before = document.querySelector('head link[rel=stylesheet], head style');

  if (before) {
    document.head.insertBefore(injected, before);
  } else {
    document.head.appendChild(injected);
  }
}

var stylesInjected = false;

var GUI = /*#__PURE__*/function () {
  /**
   * Creates a panel that holds controllers.
   * @example
   * new GUI();
   * new GUI( { container: document.getElementById( 'custom' ) } );
   *
   * @param {object} [options]
   * @param {boolean} [options.autoPlace=true]
   * Adds the GUI to `document.body` and fixes it to the top right of the page.
   *
   * @param {HTMLElement} [options.container]
   * Adds the GUI to this DOM element. Overrides `autoPlace`.
   *
   * @param {number} [options.width=245]
   * Width of the GUI in pixels, usually set when name labels become too long. Note that you can make
   * name labels wider in CSS with `.lil‑gui { ‑‑name‑width: 55% }`
   *
   * @param {string} [options.title=Controls]
   * Name to display in the title bar.
   *
   * @param {boolean} [options.injectStyles=true]
   * Injects the default stylesheet into the page if this is the first GUI.
   * Pass `false` to use your own stylesheet.
   *
   * @param {number} [options.touchStyles=true]
   * Makes controllers larger on touch devices. Pass `false` to disable touch styles.
   *
   * @param {GUI} [options.parent]
   * Adds this GUI as a child in another GUI. Usually this is done for you by `addFolder()`.
   *
   */
  function GUI() {
    var _this9 = this;

    var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        parent = _ref4.parent,
        _ref4$autoPlace = _ref4.autoPlace,
        autoPlace = _ref4$autoPlace === void 0 ? parent === undefined : _ref4$autoPlace,
        _ref4$touchStyles = _ref4.touchStyles,
        touchStyles = _ref4$touchStyles === void 0 ? true : _ref4$touchStyles,
        container = _ref4.container,
        _ref4$injectStyles = _ref4.injectStyles,
        injectStyles = _ref4$injectStyles === void 0 ? true : _ref4$injectStyles,
        _ref4$title = _ref4.title,
        title = _ref4$title === void 0 ? 'Controls' : _ref4$title,
        width = _ref4.width;

    _classCallCheck(this, GUI);

    /**
     * The GUI containing this folder, or `undefined` if this is the root GUI.
     * @type {GUI}
     */
    this.parent = parent;
    /**
     * The top level GUI containing this folder, or `this` if this is the root GUI.
     * @type {GUI}
     */

    this.root = parent ? parent.root : this;
    /**
     * The list of controllers and folders contained by this GUI.
     * @type {Array<GUI|Controller>}
     */

    this.children = [];
    /**
     * The list of controllers contained by this GUI.
     * @type {Array<Controller>}
     */

    this.controllers = [];
    /**
     * The list of folders contained by this GUI.
     * @type {Array<GUI>}
     */

    this.folders = [];
    /**
     * Used to determine if the GUI is closed. Use `gui.open()` or `gui.close()` to change this.
     * @type {boolean}
     * @readonly
     */

    this._closed = false;
    /**
     * The outermost container element.
     * @type {HTMLElement}
     */

    this.domElement = document.createElement('div');
    this.domElement.classList.add('lil-gui');
    /**
     * The DOM element that contains the title.
     * @type {HTMLElement}
     */

    this.$title = document.createElement('div');
    this.$title.classList.add('title');
    this.$title.setAttribute('role', 'button');
    this.$title.setAttribute('aria-expanded', true);
    this.$title.setAttribute('tabindex', 0);
    this.$title.addEventListener('click', function () {
      return _this9.openAnimated(_this9._closed);
    });
    this.$title.addEventListener('keydown', function (e) {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();

        _this9.$title.click();
      }
    }); // enables :active pseudo class on mobile

    this.$title.addEventListener('touchstart', function () {});
    /**
     * The DOM element that contains children.
     * @type {HTMLElement}
     */

    this.$children = document.createElement('div');
    this.$children.classList.add('children');
    this.domElement.appendChild(this.$title);
    this.domElement.appendChild(this.$children);
    this.title(title);

    if (this.parent) {
      this.parent.children.push(this);
      this.parent.folders.push(this);
      this.parent.$children.appendChild(this.domElement); // Stop the constructor early, everything onward only applies to root GUI's

      return;
    }

    this.domElement.classList.add('root'); // Inject stylesheet if we haven't done that yet

    if (!stylesInjected && injectStyles) {
      _injectStyles(stylesheet);

      stylesInjected = true;
    }

    if (container) {
      container.appendChild(this.domElement);
    } else if (autoPlace) {
      this.domElement.classList.add('autoPlace');
      document.body.appendChild(this.domElement);
    }

    if (touchStyles) {
      this.domElement.classList.add('allow-touch-styles');
    }

    if (width) {
      this.domElement.style.setProperty('--width', width + 'px');
    }
  }
  /**
   * Adds a controller to the GUI, inferring controller type using the `typeof` operator.
   * @example
   * gui.add( object, 'property' );
   * gui.add( object, 'number', 0, 100, 1 );
   * gui.add( object, 'options', [ 1, 2, 3 ] );
   *
   * @param {object} object The object the controller will modify.
   * @param {string} property Name of the property to control.
   * @param {number|object|Array} [$1] Minimum value for number controllers, or the set of
   * selectable values for a dropdown.
   * @param {number} [max] Maximum value for number controllers.
   * @param {number} [step] Step value for number controllers.
   * @returns {Controller}
   */


  _createClass(GUI, [{
    key: "add",
    value: function add(object, property, $1, max, step) {
      if (Object($1) === $1) {
        return new OptionController(this, object, property, $1);
      }

      var initialValue = object[property];

      switch (typeof initialValue) {
        case 'number':
          return new NumberController(this, object, property, $1, max, step);

        case 'boolean':
          return new BooleanController(this, object, property);

        case 'string':
          return new StringController(this, object, property);

        case 'function':
          return new FunctionController(this, object, property);
      }

      console.error("Failed to add controller for \"".concat(property, "\""), initialValue, object);
    }
    /**
     * Adds a color controller to the GUI.
     * @example
     * params = {
     * 	cssColor: '#ff00ff',
     * 	rgbColor: { r: 0, g: 0.2, b: 0.4 },
     * 	customRange: [ 0, 127, 255 ],
     * };
     *
     * gui.addColor( params, 'cssColor' );
     * gui.addColor( params, 'rgbColor' );
     * gui.addColor( params, 'customRange', 255 );
     *
     * @param {object} object The object the controller will modify.
     * @param {string} property Name of the property to control.
     * @param {number} rgbScale Maximum value for a color channel when using an RGB color. You may
     * need to set this to 255 if your colors are too dark.
     * @returns {Controller}
     */

  }, {
    key: "addColor",
    value: function addColor(object, property) {
      var rgbScale = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
      return new ColorController(this, object, property, rgbScale);
    }
    /**
     * Adds a folder to the GUI, which is just another GUI. This method returns
     * the nested GUI so you can add controllers to it.
     * @example
     * const folder = gui.addFolder( 'Position' );
     * folder.add( position, 'x' );
     * folder.add( position, 'y' );
     * folder.add( position, 'z' );
     *
     * @param {string} title Name to display in the folder's title bar.
     * @returns {GUI}
     */

  }, {
    key: "addFolder",
    value: function addFolder(title) {
      return new GUI({
        parent: this,
        title: title
      });
    }
    /**
     * Recalls values that were saved with `gui.save()`.
     * @param {object} obj
     * @param {boolean} recursive Pass false to exclude folders descending from this GUI.
     * @returns {this}
     */

  }, {
    key: "load",
    value: function load(obj) {
      var recursive = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

      if (!('controllers' in obj)) {
        throw new Error('Invalid load object. Should contain a "controllers" key.');
      }

      this.controllers.forEach(function (c) {
        if (c instanceof FunctionController) return;

        if (c._name in obj.controllers) {
          c.load(obj.controllers[c._name]);
        }
      });

      if (recursive && obj.folders) {
        this.folders.forEach(function (f) {
          if (f._title in obj.folders) {
            f.load(obj.folders[f._title]);
          }
        });
      }

      return this;
    }
    /**
     * Returns an object mapping controller names to values. The object can be passed to `gui.load()` to
     * recall these values.
     * @example
     * {
     * 	controllers: {
     * 		prop1: 1,
     * 		prop2: 'value',
     * 		...
     * 	},
     * 	folders: {
     * 		folderName1: { controllers, folders },
     * 		folderName2: { controllers, folders }
     * 		...
     * 	}
     * }
     *
     * @param {boolean} recursive Pass false to exclude folders descending from this GUI.
     * @returns {object}
     */

  }, {
    key: "save",
    value: function save() {
      var recursive = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      var obj = {
        controllers: {},
        folders: {}
      };
      this.controllers.forEach(function (c) {
        if (c instanceof FunctionController) return;

        if (c._name in obj.controllers) {
          throw new Error("Cannot save GUI with duplicate property \"".concat(c._name, "\""));
        }

        obj.controllers[c._name] = c.save();
      });

      if (recursive) {
        this.folders.forEach(function (f) {
          if (f._title in obj.folders) {
            throw new Error("Cannot save GUI with duplicate folder \"".concat(f._title, "\""));
          }

          obj.folders[f._title] = f.save();
        });
      }

      return obj;
    }
    /**
     * Opens a GUI or folder. GUI and folders are open by default.
     * @param {boolean} open Pass false to close
     * @returns {this}
     * @example
     * gui.open(); // open
     * gui.open( false ); // close
     * gui.open( gui._closed ); // toggle
     */

  }, {
    key: "open",
    value: function open() {
      var _open = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      this._closed = !_open;
      this.$title.setAttribute('aria-expanded', !this._closed);
      this.domElement.classList.toggle('closed', this._closed);
      return this;
    }
    /**
     * Closes the GUI.
     * @returns {this}
     */

  }, {
    key: "close",
    value: function close() {
      return this.open(false);
    }
  }, {
    key: "openAnimated",
    value: function openAnimated() {
      var _this10 = this;

      var open = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      // set state immediately
      this._closed = !open;
      this.$title.setAttribute('aria-expanded', !this._closed); // wait for next frame to measure $children

      requestAnimationFrame(function () {
        // explicitly set initial height for transition
        var initialHeight = _this10.$children.clientHeight;
        _this10.$children.style.height = initialHeight + 'px';

        _this10.domElement.classList.add('transition');

        var onTransitionEnd = function onTransitionEnd(e) {
          if (e.target !== _this10.$children) return;
          _this10.$children.style.height = '';

          _this10.domElement.classList.remove('transition');

          _this10.$children.removeEventListener('transitionend', onTransitionEnd);
        };

        _this10.$children.addEventListener('transitionend', onTransitionEnd); // todo: this is wrong if children's scrollHeight makes for a gui taller than maxHeight


        var targetHeight = !open ? 0 : _this10.$children.scrollHeight;

        _this10.domElement.classList.toggle('closed', !open);

        requestAnimationFrame(function () {
          _this10.$children.style.height = targetHeight + 'px';
        });
      });
      return this;
    }
    /**
     * Change the title of this GUI.
     * @param {string} title
     * @returns {this}
     */

  }, {
    key: "title",
    value: function title(_title) {
      /**
       * Current title of the GUI. Use `gui.title( 'Title' )` to modify this value.
       * @type {string}
       */
      this._title = _title;
      this.$title.innerHTML = _title;
      return this;
    }
    /**
     * Resets all controllers to their initial values.
     * @param {boolean} recursive Pass false to exclude folders descending from this GUI.
     * @returns {this}
     */

  }, {
    key: "reset",
    value: function reset() {
      var recursive = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      var controllers = recursive ? this.controllersRecursive() : this.controllers;
      controllers.forEach(function (c) {
        return c.reset();
      });
      return this;
    }
    /**
     * Pass a function to be called whenever a controller in this GUI changes.
     * @param {function({object:object, property:string, value:any, controller:Controller})} callback
     * @returns {this}
     * @example
     * gui.onChange( event => {
     * 	event.object     // object that was modified
     * 	event.property   // string, name of property
     * 	event.value      // new value of controller
     * 	event.controller // controller that was modified
     * } );
     */

  }, {
    key: "onChange",
    value: function onChange(callback) {
      this._onChange = callback;
      return this;
    }
  }, {
    key: "_callOnChange",
    value: function _callOnChange(controller) {
      if (this.parent) {
        this.parent._callOnChange(controller);
      }

      if (this._onChange !== undefined) {
        this._onChange.call(this, {
          object: controller.object,
          property: controller.property,
          value: controller.getValue(),
          controller: controller
        });
      }
    }
    /**
     * Destroys all DOM elements and event listeners associated with this GUI
     */

  }, {
    key: "destroy",
    value: function destroy() {
      if (this.parent) {
        this.parent.children.splice(this.parent.children.indexOf(this), 1);
        this.parent.folders.splice(this.parent.folders.indexOf(this), 1);
      }

      if (this.domElement.parentElement) {
        this.domElement.parentElement.removeChild(this.domElement);
      }

      Array.from(this.children).forEach(function (c) {
        return c.destroy();
      });

      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }
    }
    /**
     * Returns an array of controllers contained by this GUI and its descendents.
     * @returns {Controller[]}
     */

  }, {
    key: "controllersRecursive",
    value: function controllersRecursive() {
      var controllers = Array.from(this.controllers);
      this.folders.forEach(function (f) {
        controllers = controllers.concat(f.controllersRecursive());
      });
      return controllers;
    }
    /**
     * Returns an array of folders contained by this GUI and its descendents.
     * @returns {GUI[]}
     */

  }, {
    key: "foldersRecursive",
    value: function foldersRecursive() {
      var folders = Array.from(this.folders);
      this.folders.forEach(function (f) {
        folders = folders.concat(f.foldersRecursive());
      });
      return folders;
    }
  }]);

  return GUI;
}();

export default GUI;
export { BooleanController, ColorController, Controller, FunctionController, GUI, NumberController, OptionController, StringController };